/**
 * Pure helpers over `Drawing`s: creation, cloning, sanitising stored documents, visibility, z-order
 * and the per-drawing three-way merge used when a `drawings/{root}` save hits a 409.
 */
import type { BarSpec } from "../bars/barBuilder.ts";
import { visibilityUnit } from "../indicators/state.ts";
import { VISIBILITY_UNITS, type Visibility } from "../indicators/types.ts";
import { HEX, sanitizeLevels } from "./fib.ts";
import { factoryStyle, isDrawingType, toolSpec } from "./tools.ts";
import {
  MAX_DRAWINGS_PER_ROOT,
  MAX_POINTS,
  type Drawing,
  type DrawingPoint,
  type DrawingStyle,
  type DrawingType,
  type LineStyle,
} from "./types.ts";

/** Stored body of `drawings/{root}` (version 1). */
export type DrawingDocBody = { v: 1; drawings: Drawing[] };

export function newDrawingId(): string {
  const c = (globalThis as { crypto?: { randomUUID?: () => string } }).crypto;
  const u = c?.randomUUID?.() ?? `${Math.random().toString(16).slice(2)}${Date.now().toString(16)}`;
  return `d-${u.replace(/-/g, "").slice(0, 12)}`;
}

/** A new drawing of `type` at `points`, with the tool's style (or a saved default `style`). */
export function createDrawing(
  type: DrawingType,
  points: DrawingPoint[],
  opts: { style?: DrawingStyle; z?: number; now?: number; scope?: string } = {},
): Drawing {
  const now = opts.now ?? Date.now();
  const d: Drawing = {
    id: newDrawingId(),
    type,
    points: points.map((p) => ({ t: Math.round(p.t), p: Math.round(p.p) })),
    style: cloneStyle(opts.style ?? factoryStyle(type)),
    text: toolSpec(type).text ?? "",
    locked: false,
    hidden: false,
    z: opts.z ?? 0,
    created: now,
    updated: now,
  };
  if (opts.scope !== undefined) d.scope = opts.scope;
  return d;
}

export function cloneStyle(s: DrawingStyle): DrawingStyle {
  const out: DrawingStyle = { ...s };
  if (s.levels) out.levels = s.levels.map((l) => ({ ...l }));
  return out;
}

/** A copy with a fresh id (clone / paste), placed on top. */
export function cloneDrawing(d: Drawing, z: number, now = Date.now()): Drawing {
  const c: Drawing = {
    ...d,
    id: newDrawingId(),
    points: d.points.map((p) => ({ ...p })),
    style: cloneStyle(d.style),
    z,
    locked: false,
    created: now,
    updated: now,
  };
  if (d.visibility) c.visibility = { ...d.visibility };
  if (d.anchor) c.anchor = { ...d.anchor };
  delete c.name;
  return c;
}

/** Display name (object tree): the user's name or the tool's. */
export function drawingName(d: Drawing): string {
  return d.name ?? toolSpec(d.type).name;
}

/** Shown on a chart of bar type `spec` (hidden flag and the Visibility tab). */
export function isVisibleOn(d: Drawing, spec: BarSpec): boolean {
  return !d.hidden && d.visibility?.[visibilityUnit(spec)] !== false;
}

/** Belongs on the chart with key `chartKey` (scope absent = every chart of the root). */
export function inScope(d: Drawing, chartKey: string | null): boolean {
  return d.scope === undefined || d.scope === chartKey;
}

// ---------------------------------------------------------------------------------- z-order

export function topZ(list: readonly Drawing[]): number {
  let z = 0;
  for (const d of list) z = Math.max(z, d.z);
  return z;
}

export type ZMove = "front" | "back" | "forward" | "backward";

/**
 * New z values after moving `ids` (TradingView "Bring to front / Send to back / Bring forward /
 * Send backward"). Returns the drawings whose z changed (renumbered 1..n, stable order).
 */
export function reorder(list: readonly Drawing[], ids: ReadonlySet<string>, move: ZMove): Drawing[] {
  const sorted = [...list].sort((a, b) => a.z - b.z);
  let order: Drawing[];
  if (move === "front")
    order = [...sorted.filter((d) => !ids.has(d.id)), ...sorted.filter((d) => ids.has(d.id))];
  else if (move === "back")
    order = [...sorted.filter((d) => ids.has(d.id)), ...sorted.filter((d) => !ids.has(d.id))];
  else {
    order = [...sorted];
    if (move === "forward") {
      for (let i = order.length - 2; i >= 0; i--) {
        const a = order[i];
        const b = order[i + 1];
        if (a && b && ids.has(a.id) && !ids.has(b.id)) {
          order[i] = b;
          order[i + 1] = a;
        }
      }
    } else {
      for (let i = 1; i < order.length; i++) {
        const a = order[i - 1];
        const b = order[i];
        if (a && b && ids.has(b.id) && !ids.has(a.id)) {
          order[i - 1] = b;
          order[i] = a;
        }
      }
    }
  }
  const changed: Drawing[] = [];
  order.forEach((d, i) => {
    if (d.z !== i + 1) changed.push({ ...d, z: i + 1 });
  });
  return changed;
}

// ---------------------------------------------------------------------------------- sanitise

const LINE_STYLES: readonly LineStyle[] = ["solid", "dashed", "dotted"];

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === "object" && v !== null && !Array.isArray(v);
}

/** A stored style merged over the tool's factory style (invalid fields dropped). */
export function sanitizeStyle(type: DrawingType, raw: unknown): DrawingStyle {
  const s = factoryStyle(type);
  if (!isRecord(raw)) return s;
  const color = (k: "color" | "textColor"): void => {
    const v = raw[k];
    if (typeof v === "string" && HEX.test(v)) s[k] = v;
  };
  color("color");
  color("textColor");
  if (raw["fill"] === null) s.fill = null;
  else if (typeof raw["fill"] === "string" && HEX.test(raw["fill"])) s.fill = raw["fill"];
  const w = raw["width"];
  if (typeof w === "number" && Number.isFinite(w)) s.width = Math.min(8, Math.max(1, Math.round(w)));
  if (typeof raw["line"] === "string" && LINE_STYLES.includes(raw["line"] as LineStyle))
    s.line = raw["line"] as LineStyle;
  for (const k of ["extendLeft", "extendRight", "showPrice", "showInfo"] as const) {
    const v = raw[k];
    if (typeof v === "boolean") s[k] = v;
  }
  for (const k of ["fibPrices", "reverse", "trendLine", "arrowEnd", "middle"] as const) {
    const v = raw[k];
    if (typeof v === "boolean") s[k] = v;
  }
  if (raw["fibLabels"] === "values" || raw["fibLabels"] === "percent" || raw["fibLabels"] === "none")
    s.fibLabels = raw["fibLabels"];
  if (raw["direction"] === "up" || raw["direction"] === "down") s.direction = raw["direction"];
  const qty = raw["qty"];
  if (typeof qty === "number" && Number.isInteger(qty) && qty >= 1 && qty <= 100_000) s.qty = qty;
  const dev = raw["deviations"];
  if (typeof dev === "number" && Number.isFinite(dev) && dev >= 0 && dev <= 10) s.deviations = dev;
  if (s.levels) s.levels = sanitizeLevels(raw["levels"], s.levels);
  return s;
}

function sanitizePoint(raw: unknown): DrawingPoint | null {
  if (!isRecord(raw)) return null;
  const t = raw["t"];
  const p = raw["p"];
  if (typeof t !== "number" || typeof p !== "number" || !Number.isFinite(t) || !Number.isFinite(p))
    return null;
  // Prices are integer ticks: anything else is rounded, never kept as a float.
  return { t: Math.round(t), p: Math.round(p) };
}

function sanitizeVisibility(raw: unknown): Visibility | undefined {
  if (!isRecord(raw)) return undefined;
  const out: Visibility = {};
  let any = false;
  for (const u of VISIBILITY_UNITS) {
    if (raw[u] === false) {
      out[u] = false;
      any = true;
    }
  }
  return any ? out : undefined;
}

/** One stored drawing, or null when unusable (unknown tool, too few points). */
export function sanitizeDrawing(raw: unknown): Drawing | null {
  if (!isRecord(raw)) return null;
  const type = raw["type"];
  const id = raw["id"];
  if (!isDrawingType(type) || typeof id !== "string" || !/^[\w-]{1,40}$/.test(id)) return null;
  const pts = Array.isArray(raw["points"]) ? raw["points"].slice(0, MAX_POINTS) : [];
  const points: DrawingPoint[] = [];
  for (const r of pts) {
    const p = sanitizePoint(r);
    if (p) points.push(p);
  }
  if (points.length < minPoints(type)) return null;
  const num = (k: string, d: number): number => {
    const v = raw[k];
    return typeof v === "number" && Number.isFinite(v) ? v : d;
  };
  const d: Drawing = {
    id,
    type,
    points,
    style: sanitizeStyle(type, raw["style"]),
    text: typeof raw["text"] === "string" ? raw["text"].slice(0, 2000) : "",
    locked: raw["locked"] === true,
    hidden: raw["hidden"] === true,
    z: num("z", 0),
    created: num("created", 0),
    updated: num("updated", 0),
  };
  if (typeof raw["name"] === "string" && raw["name"].trim()) d.name = raw["name"].slice(0, 80);
  const vis = sanitizeVisibility(raw["visibility"]);
  if (vis) d.visibility = vis;
  if (typeof raw["scope"] === "string" && raw["scope"]) d.scope = raw["scope"].slice(0, 120);
  const a = raw["anchor"];
  if (isRecord(a) && typeof a["fx"] === "number" && typeof a["fy"] === "number")
    d.anchor = { fx: clamp01(a["fx"]), fy: clamp01(a["fy"]) };
  return d;
}

function clamp01(v: number): number {
  return Number.isFinite(v) ? Math.min(1, Math.max(0, v)) : 0.5;
}

/** Points a finished drawing needs (long/short: entry, target, stop). */
export function minPoints(type: DrawingType): number {
  if (type === "long_position" || type === "short_position") return 3;
  return toolSpec(type).points;
}

/** A stored `drawings/{root}` body → drawings (bad entries dropped, ids unique, capped). */
export function sanitizeDrawingDoc(raw: unknown): Drawing[] {
  if (!isRecord(raw) || !Array.isArray(raw["drawings"])) return [];
  const seen = new Set<string>();
  const out: Drawing[] = [];
  for (const r of raw["drawings"]) {
    const d = sanitizeDrawing(r);
    if (!d || seen.has(d.id)) continue;
    seen.add(d.id);
    out.push(d);
    if (out.length >= MAX_DRAWINGS_PER_ROOT) break;
  }
  return out;
}

export function drawingDocBody(list: readonly Drawing[]): DrawingDocBody {
  return { v: 1, drawings: [...list].sort((a, b) => a.z - b.z) };
}

// ---------------------------------------------------------------------------------- merge

const same = (a: unknown, b: unknown): boolean => JSON.stringify(a) === JSON.stringify(b);

/**
 * Three-way merge per drawing id (the `drawings` doc type is whole-document on the server; the
 * client merges instead of dropping either side): a drawing only one side changed takes that
 * side (including deletion); one both sides changed takes **local** (the edit just made here).
 */
export function mergeDrawings(
  base: readonly Drawing[],
  local: readonly Drawing[],
  server: readonly Drawing[],
): Drawing[] {
  const b = new Map(base.map((d) => [d.id, d]));
  const l = new Map(local.map((d) => [d.id, d]));
  const s = new Map(server.map((d) => [d.id, d]));
  const out: Drawing[] = [];
  for (const id of new Set([...l.keys(), ...s.keys(), ...b.keys()])) {
    const bd = b.get(id);
    const ld = l.get(id);
    const sd = s.get(id);
    const localChanged = !same(bd, ld);
    const pick = localChanged ? ld : sd;
    if (pick) out.push(pick);
  }
  return out.sort((x, y) => x.z - y.z);
}
